/**
 * ContractorStatusPanel — P4 contractor-facing transparency surface.
 *
 * Shows the contractor their reliability tier, plain-language metrics,
 * and "how to get more work" guidance. NO percentages or formulas; sample
 * size < 3 hides metrics entirely and shows the "still learning" message
 * (locked rule #7).
 *
 * Pure read-only consumer of canonical resolvers:
 *   - getContractorOfferMetrics()
 *   - getReliabilityTierLabel/Description()
 *   - getContractorReliability() (existing tier engine)
 *
 * No financial leakage: never references contractValue, margin, or any
 * internal pricing field.
 */

import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Award, ShieldCheck, Eye, ShieldAlert, Sparkles, Info } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import {
  getContractorOfferMetrics,
  getReliabilityTierLabel,
  getReliabilityTierDescription,
  MIN_SAMPLE_SIZE,
} from "@/data/contractor/offerMetrics";
import {
  getContractorReliability,
  RELIABILITY_CONFIG,
  type ReliabilityTier,
} from "@/data/contractorReliability";
import {
  STATUS_PANEL,
  HOW_TO_GET_MORE_WORK,
} from "@/data/contractor/contractorMessaging";

const TIER_ICON: Record<ReliabilityTier, typeof Award> = {
  Elite: Award,
  Reliable: ShieldCheck,
  Watch: Eye,
  Risk: ShieldAlert,
};

/* Plain-language metric formatters. Spec: "no percentages or formulas"
 * inside the "How to get more work" section, but the metrics block uses
 * plain qualitative bands rather than raw % to stay accessible. */
function bandFromRate(
  rate: number | null,
  bands: { strong: number; ok: number },
): "Strong" | "OK" | "Needs work" | "—" {
  if (rate == null) return "—";
  if (rate >= bands.strong) return "Strong";
  if (rate >= bands.ok) return "OK";
  return "Needs work";
}
function inverseBand(
  rate: number | null,
  bands: { strong: number; ok: number },
): "Strong" | "OK" | "Needs work" | "—" {
  if (rate == null) return "—";
  if (rate <= bands.strong) return "Strong";
  if (rate <= bands.ok) return "OK";
  return "Needs work";
}
function responseBand(ms: number | null): "Strong" | "OK" | "Needs work" | "—" {
  if (ms == null) return "—";
  const minutes = ms / 60000;
  if (minutes <= 30) return "Strong";
  if (minutes <= 120) return "OK";
  return "Needs work";
}

const TONE: Record<string, string> = {
  Strong: "text-success",
  OK: "text-foreground",
  "Needs work": "text-warning",
  "—": "text-muted-foreground",
};

export function ContractorStatusPanel() {
  const {
    contractors,
    opportunities,
    jobs,
    issues,
    credits,
    marketplaceOffers,
    systemEvents,
  } = useAppData();
  const { matchKey } = useCurrentContractor();

  const contractor = useMemo(() => {
    if (!matchKey) return null;
    return (
      contractors.find(
        (c) =>
          c.id === matchKey ||
          c.companyName.toLowerCase() === matchKey.toLowerCase(),
      ) ?? null
    );
  }, [contractors, matchKey]);

  const data = useMemo(() => {
    if (!contractor) return null;
    const scorecard = computeScorecard(
      contractor,
      opportunities,
      jobs,
      issues,
      credits,
      marketplaceOffers,
    );
    const reliability = getContractorReliability(scorecard);
    const metrics = getContractorOfferMetrics(
      contractor.id,
      marketplaceOffers,
      jobs,
      systemEvents,
    );
    return { reliability, metrics };
  }, [
    contractor,
    opportunities,
    jobs,
    issues,
    credits,
    marketplaceOffers,
    systemEvents,
  ]);

  if (!contractor || !data) return null;

  const { reliability, metrics } = data;
  const insufficient = metrics.sampleSize < MIN_SAMPLE_SIZE;
  const tier = reliability.tier;
  const tierConfig = RELIABILITY_CONFIG[tier];
  const Icon = TIER_ICON[tier];

  return (
    <Card data-testid="contractor-status-panel">
      <CardContent className="p-4 space-y-4">
        {/* Heading */}
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> {STATUS_PANEL.heading}
          </h2>
        </div>

        {/* Tier */}
        <div
          data-testid="status-tier"
          data-tier={tier}
          className={`rounded-lg border ${tierConfig.border} ${tierConfig.bg} px-3 py-2.5 flex items-start gap-2.5`}
        >
          <Icon className={`h-5 w-5 ${tierConfig.color} shrink-0 mt-0.5`} />
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-bold ${tierConfig.color}`}>
              {getReliabilityTierLabel(tier)}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {STATUS_PANEL.tierExplain}
            </p>
            <p className="text-[11px] text-foreground/80 mt-1">
              {getReliabilityTierDescription(tier)}
            </p>
          </div>
        </div>

        {/* Metrics OR still-learning message */}
        {insufficient ? (
          <div
            data-testid="status-still-learning"
            className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2.5 flex items-start gap-2"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-[11px] text-muted-foreground">
              {STATUS_PANEL.stillLearning}
            </p>
          </div>
        ) : (
          <div data-testid="status-metrics" className="grid grid-cols-2 gap-2">
            <MetricCell
              label={STATUS_PANEL.metrics.acceptance}
              band={bandFromRate(metrics.acceptanceRate, { strong: 0.8, ok: 0.5 })}
            />
            <MetricCell
              label={STATUS_PANEL.metrics.responseTime}
              band={responseBand(metrics.medianResponseTimeMs)}
            />
            <MetricCell
              label={STATUS_PANEL.metrics.expiry}
              band={inverseBand(metrics.expiryRate, { strong: 0.1, ok: 0.3 })}
            />
            <MetricCell
              label={STATUS_PANEL.metrics.postAcceptReliability}
              band={inverseBand(metrics.postAcceptReassignmentRate, {
                strong: 0.05,
                ok: 0.2,
              })}
            />
          </div>
        )}

        {/* How to get more work */}
        <div
          data-testid="how-to-get-more-work"
          className="rounded-md border border-border bg-background px-3 py-2.5"
        >
          <p className="text-[11px] font-bold text-foreground mb-1.5">
            {HOW_TO_GET_MORE_WORK.heading}
          </p>
          <ul className="space-y-1">
            {HOW_TO_GET_MORE_WORK.bullets.map((b) => (
              <li
                key={b}
                className="text-[11px] text-muted-foreground flex items-start gap-1.5"
              >
                <span className="text-success mt-0.5">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCell({ label, band }: { label: string; band: string }) {
  return (
    <div className="rounded-md border border-border bg-background/50 px-2.5 py-2">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`text-sm font-bold ${TONE[band]}`}>{band}</p>
    </div>
  );
}
